import * as React from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import { useToast } from "../../../components/shared/ToastProvider";

/**
 * Contract Form Modal Component
 * Reusable modal for creating/updating condo unit contracts
 * UI only - Backend developer will add form handling and API calls
 */
export default function ContractFormModal({
  open,
  onClose,
  title = "Add Contract",
  primaryActionLabel = "Create"
}) {
  const { showToast } = useToast();

  const handlePrimaryClick = () => {
    // UI-only: backend will handle actual save later.
    showToast({
      message: primaryActionLabel === "Create" ? "Contract created" : "Contract updated",
      severity: "success"
    });
    if (onClose) {
      onClose();
    }
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handlePrimaryClick}>
        {primaryActionLabel}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      actions={actions}
      maxWidth="md"
    >
      <div className="modal-form-grid" style={{ marginTop: 8 }}>
        {/* Two-column layout: left strictly follows legacy left column order */}
        <div className="modal-form-row modal-form-row--two-cols">
          {/* Left column */}
          <div>
            <FormControl fullWidth required size="small">
              <InputLabel>Condo Unit</InputLabel>
              <Select defaultValue="" label="Condo Unit">
                <MenuItem value="">-</MenuItem>
                <MenuItem value="1003-oriental">1003 Oriental KH</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required size="small" style={{ marginTop: 8 }}>
              <InputLabel>Rental Currency</InputLabel>
              <Select defaultValue="PHP" label="Rental Currency">
                <MenuItem value="PHP">PHP</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="KHR">KHR</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required
              size="small"
              label="Rental Fee"
              type="number"
              inputProps={{ step: "0.01" }}
              style={{ marginTop: 8 }}
            />

            <FormControl fullWidth required size="small" style={{ marginTop: 8 }}>
              <InputLabel>Payment Term</InputLabel>
              <Select defaultValue="1-month" label="Payment Term">
                <MenuItem value="1-month">1 month</MenuItem>
                <MenuItem value="3-months">3 months</MenuItem>
                <MenuItem value="6-months">6 months</MenuItem>
                <MenuItem value="12-months">12 months</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required
              size="small"
              label="Contract Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              required
              size="small"
              label="Contract End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              required
              size="small"
              label="Lessee"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              required
              size="small"
              label="Lessee Address"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Lessor"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Lessor Phone"
              style={{ marginTop: 8 }}
            />

            <FormControl fullWidth size="small" style={{ marginTop: 8 }}>
              <InputLabel>Lessor Preferred Currency</InputLabel>
              <Select defaultValue="" label="Lessor Preferred Currency">
                <MenuItem value="">-</MenuItem>
                <MenuItem value="PHP">PHP</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="KHR">KHR</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Lessor Bank Acct Name"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Lessor Bank Acct No"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Lessor Bank Name"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Lessor Bank Address"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Bank Swift Code"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Remarks"
              multiline
              rows={2}
              style={{ marginTop: 8 }}
            />
          </div>

          {/* Right column: security, advance, broker (strict legacy order) */}
          <div>
            <TextField
              fullWidth
              required
              size="small"
              label="Security Deposit"
              type="number"
              inputProps={{ step: "0.01" }}
            />

            <TextField
              fullWidth
              size="small"
              label="Security Deposit Returned Amount"
              type="number"
              inputProps={{ step: "0.01" }}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Security Deposit Returned Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Security Deposit Expense"
              type="number"
              inputProps={{ step: "0.01" }}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              required
              size="small"
              label="Security Months"
              type="number"
              defaultValue={0}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Security Ref No"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Security Receipt Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              style={{ marginTop: 8 }}
            />

            <FormControl fullWidth size="small" style={{ marginTop: 8 }}>
              <InputLabel>Skip Security Deposit Alert</InputLabel>
              <Select defaultValue="no" label="Skip Security Deposit Alert">
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Termination Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              required
              size="small"
              label="Advance Payment"
              type="number"
              inputProps={{ step: "0.01" }}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              required
              size="small"
              label="Advance Months"
              type="number"
              defaultValue={0}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Advance Reference Number"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Advance Receipt Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Broker Name"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Broker Phone Number"
              style={{ marginTop: 8 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Broker E-mail"
              style={{ marginTop: 8 }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
